/**
 * Privacy Policy Page — Public
 * Bilingual (EN/PT-BR) privacy policy for charstan.com
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Shield, Lock, Database, Eye, Server, Globe, Mail, ArrowLeft } from 'lucide-react';
import PublicLanguageSwitcher from '../components/PublicLanguageSwitcher';

const sections = [
  { key: 'data_collection', icon: Database },
  { key: 'data_usage', icon: Eye },
  { key: 'data_storage', icon: Server },
  { key: 'data_protection', icon: Lock },
  { key: 'data_sharing', icon: Globe },
  { key: 'user_rights', icon: Shield },
];

const PrivacyPolicyPage = () => {
  const { t, i18n } = useTranslation('privacy');
  const isPt = i18n.language === 'pt-BR';

  const content = {
    en: {
      title: 'Privacy Policy',
      subtitle: 'How CHARSTAN protects and handles your data',
      lastUpdated: 'Last updated: April 2026',
      sections: [
        {
          key: 'data_collection',
          title: 'Data We Collect',
          body: 'We collect information you provide directly, such as your name, email address, organization details, and role when you create an account or interact with our platform.',
          bullets: [
            'Account registration information (name, email, organization)',
            'QMS workflow data you create (documents, CAPAs, audits, deviations)',
            'Usage analytics (pages visited, features used, session duration)',
            'Technical data (browser type, IP address, device information)',
          ],
        },
        {
          key: 'data_usage',
          title: 'How We Use Your Data',
          body: 'Your data is used solely to provide, maintain, and improve CHARSTAN Intelligence Platform services.',
          bullets: [
            'Delivering QMS platform functionality and AI-powered insights',
            'Maintaining audit trails and regulatory compliance records',
            'Improving platform performance and user experience',
            'Sending critical system notifications and compliance alerts',
            'Generating anonymized, aggregated analytics for service improvement',
          ],
        },
        {
          key: 'data_storage',
          title: 'Data Storage & Retention',
          body: 'All data is stored in encrypted, SOC 2-compliant cloud infrastructure with geographic controls.',
          bullets: [
            'Data encrypted at rest (AES-256) and in transit (TLS 1.3)',
            'Database backups with point-in-time recovery',
            'Data retained as required by applicable regulatory frameworks (e.g., GxP, FDA 21 CFR Part 11)',
            'Account data deleted upon verified request, subject to legal retention requirements',
          ],
        },
        {
          key: 'data_protection',
          title: 'Data Protection Measures',
          body: 'We implement enterprise-grade security controls to protect your information.',
          bullets: [
            'Role-based access control (RBAC) with principle of least privilege',
            'Session management with secure, HTTP-only cookies',
            'Regular security assessments and penetration testing',
            'Incident response procedures aligned with ISO 27001',
            'Employee security training and background checks',
          ],
        },
        {
          key: 'data_sharing',
          title: 'Data Sharing & Third Parties',
          body: 'We do not sell your data. We share data only as described below.',
          bullets: [
            'AI service providers (OpenAI, Anthropic) — for platform intelligence features only, under strict data processing agreements',
            'Email delivery (SendGrid) — for transactional notifications only',
            'Cloud infrastructure providers — for hosting and data storage',
            'Legal or regulatory authorities — when required by law',
          ],
        },
        {
          key: 'user_rights',
          title: 'Your Rights',
          body: 'You have the following rights regarding your personal data:',
          bullets: [
            'Right to access — request a copy of your personal data',
            'Right to rectification — correct inaccurate information',
            'Right to erasure — request deletion of your data (subject to legal retention)',
            'Right to data portability — receive your data in a structured format',
            'Right to object — opt out of specific data processing activities',
            'Right to withdraw consent — at any time, without affecting prior processing',
          ],
        },
      ],
      contact: {
        title: 'Contact Us',
        body: 'For privacy-related inquiries, data access requests, or to exercise your rights:',
        email: 'privacy@charstan.com',
      },
    },
    'pt-BR': {
      title: 'Política de Privacidade',
      subtitle: 'Como a CHARSTAN protege e trata seus dados',
      lastUpdated: 'Última atualização: Abril 2026',
      sections: [
        {
          key: 'data_collection',
          title: 'Dados que Coletamos',
          body: 'Coletamos informações que você fornece diretamente, como nome, e-mail, detalhes da organização e cargo ao criar uma conta ou interagir com nossa plataforma.',
          bullets: [
            'Informações de registro (nome, e-mail, organização)',
            'Dados de fluxo de trabalho QMS (documentos, CAPAs, auditorias, desvios)',
            'Analíticos de uso (páginas visitadas, recursos utilizados, duração da sessão)',
            'Dados técnicos (tipo de navegador, endereço IP, informações do dispositivo)',
          ],
        },
        {
          key: 'data_usage',
          title: 'Como Usamos Seus Dados',
          body: 'Seus dados são usados exclusivamente para fornecer, manter e melhorar os serviços da Plataforma de Inteligência CHARSTAN.',
          bullets: [
            'Entrega de funcionalidades da plataforma QMS e insights com IA',
            'Manutenção de trilhas de auditoria e registros de conformidade regulatória',
            'Melhoria do desempenho da plataforma e experiência do usuario',
            'Envio de notificações críticas do sistema e alertas de conformidade',
            'Geração de analíticos anonimizados e agregados para melhoria do serviço',
          ],
        },
        {
          key: 'data_storage',
          title: 'Armazenamento e Retenção de Dados',
          body: 'Todos os dados são armazenados em infraestrutura de nuvem criptografada e em conformidade com SOC 2.',
          bullets: [
            'Dados criptografados em repouso (AES-256) e em trânsito (TLS 1.3)',
            'Backups de banco de dados com recuperacao point-in-time',
            'Dados retidos conforme exigido pelas estruturas regulatórias aplicáveis (ex: GxP, FDA 21 CFR Part 11)',
            'Dados da conta excluídos mediante solicitação verificada, sujeito a requisitos legais de retenção',
          ],
        },
        {
          key: 'data_protection',
          title: 'Medidas de Proteção de Dados',
          body: 'Implementamos controles de segurança de nível empresarial para proteger suas informações.',
          bullets: [
            'Controle de acesso baseado em funções (RBAC) com princípio de menor privilegio',
            'Gerenciamento de sessões com cookies seguros e HTTP-only',
            'Avaliações de segurança regulares e testes de penetracao',
            'Procedimentos de resposta a incidentes alinhados com ISO 27001',
            'Treinamento de segurança para funcionários e verificação de antecedentes',
          ],
        },
        {
          key: 'data_sharing',
          title: 'Compartilhamento de Dados e Terceiros',
          body: 'Não vendemos seus dados. Compartilhamos dados apenas conforme descrito abaixo.',
          bullets: [
            'Provedores de serviços de IA (OpenAI, Anthropic) — apenas para recursos de inteligência da plataforma, sob acordos rigorosos de processamento de dados',
            'Entrega de e-mail (SendGrid) — apenas para notificações transacionais',
            'Provedores de infraestrutura de nuvem — para hospedagem e armazenamento de dados',
            'Autoridades legais ou regulatórias — quando exigido por lei',
          ],
        },
        {
          key: 'user_rights',
          title: 'Seus Direitos',
          body: 'Você tem os seguintes direitos em relação aos seus dados pessoais:',
          bullets: [
            'Direito de acesso — solicitar uma copia dos seus dados pessoais',
            'Direito de retificação — corrigir informações imprecisas',
            'Direito de exclusão — solicitar a exclusão dos seus dados (sujeito a retenção legal)',
            'Direito a portabilidade — receber seus dados em formato estruturado',
            'Direito de objeção — recusar atividades específicas de processamento de dados',
            'Direito de retirar consentimento — a qualquer momento, sem afetar o processamento anterior',
          ],
        },
      ],
      contact: {
        title: 'Contato',
        body: 'Para consultas relacionadas a privacidade, solicitações de acesso a dados ou para exercer seus direitos:',
        email: 'privacy@charstan.com',
      },
    },
  };

  const c = isPt ? content['pt-BR'] : content.en;

  return (
    <div className="min-h-screen bg-[#0A0E17] text-white" data-testid="privacy-page">
      {/* Header */}
      <header className="border-b border-[rgba(255,255,255,0.06)] bg-[#0A0E17]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-[#36A9E1] transition-colors" data-testid="privacy-back-home">
            <ArrowLeft size={18} />
            <span className="font-semibold text-lg tracking-tight">CHARSTAN</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/trust" className="text-sm text-[#8B95A5] hover:text-white transition-colors">
              {isPt ? 'Central de Confiança' : 'Trust Center'}
            </Link>
            <Link to="/standards" className="text-sm text-[#8B95A5] hover:text-white transition-colors">
              {isPt ? 'Normas' : 'Standards'}
            </Link>
            <PublicLanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-[rgba(54,169,225,0.1)]">
            <Lock className="text-[#36A9E1]" size={28} />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold" data-testid="privacy-title">{c.title}</h1>
            <p className="text-[#8B95A5] text-sm mt-1">{c.lastUpdated}</p>
          </div>
        </div>
        <p className="text-[#A0AEC0] text-lg max-w-2xl">{c.subtitle}</p>
      </section>

      {/* Sections */}
      <main className="max-w-5xl mx-auto px-6 pb-20 space-y-6">
        {c.sections.map((section, idx) => {
          const Icon = sections[idx]?.icon || Shield;
          return (
            <section
              key={section.key}
              className="bg-[#111827] border border-[rgba(255,255,255,0.06)] rounded-xl p-6 hover:border-[rgba(54,169,225,0.2)] transition-colors"
              data-testid={`privacy-section-${section.key}`}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 rounded-lg bg-[rgba(54,169,225,0.1)] shrink-0">
                  <Icon className="text-[#36A9E1]" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">{section.title}</h2>
                  <p className="text-[#A0AEC0] mt-2 leading-relaxed">{section.body}</p>
                </div>
              </div>
              {section.bullets && (
                <ul className="ml-12 space-y-2">
                  {section.bullets.map((b, i) => (
                    <li key={i} className="text-[#8B95A5] text-sm flex items-start gap-2">
                      <span className="text-[#36A9E1] mt-0.5 shrink-0">&#8226;</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}

        {/* Contact */}
        <section className="bg-[#111827] border border-[rgba(54,169,225,0.15)] rounded-xl p-6" data-testid="privacy-contact">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[rgba(54,169,225,0.1)] shrink-0">
              <Mail className="text-[#36A9E1]" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{c.contact.title}</h2>
              <p className="text-[#A0AEC0] mt-2">{c.contact.body}</p>
              <a
                href={`mailto:${c.contact.email}`}
                className="inline-flex items-center gap-2 mt-3 text-[#36A9E1] hover:text-[#38BDF8] transition-colors font-medium"
                data-testid="privacy-contact-email"
              >
                <Mail size={16} />
                {c.contact.email}
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-[rgba(255,255,255,0.06)] bg-[#0A0E17] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-[#4A5568] text-sm">&copy; 2026 CHARSTAN. {isPt ? 'Todos os direitos reservados.' : 'All rights reserved.'}</span>
          <div className="flex items-center gap-6">
            <Link to="/trust" className="text-[#8B95A5] hover:text-white text-sm transition-colors">{isPt ? 'Confiança' : 'Trust'}</Link>
            <Link to="/governance" className="text-[#8B95A5] hover:text-white text-sm transition-colors">{isPt ? 'Governança' : 'Governance'}</Link>
            <Link to="/help" className="text-[#8B95A5] hover:text-white text-sm transition-colors">{isPt ? 'Ajuda' : 'Help'}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
